import { TFormatted, TCollection, TCompiled, TEntity, TEntityRef, TNote } from '../../types';
import { getSha1Hash } from '../getSha1Hash';

import { parser } from './markdown';

export type TGetNodesByRefs = (refs: any[]) => (TCollection | TNote | undefined)[];

/**
 * Сообщает о ссылке, для которой не нашлось узла.
 *
 * Обход такие ссылки пропускает — для экспорта это безобидно, а для потребителя,
 * который по пустому результату делает вывод «здесь ничего нет», уже нет:
 * пропущенное поддерево неотличимо от отсутствующего содержимого. Колбэк
 * необязателен, чтобы существующие вызывающие не менялись.
 */
export type TOnMissingRef = (ref: TEntityRef) => void;

export function getCompiledContentOfEntity(
  entity: TCollection | TNote,
  getNodesByRefs: TGetNodesByRefs,
  onMissingRef?: TOnMissingRef,
): TCompiled {
  let compiledText: TFormatted[] = [];
  if (entity.type === 'note') {
    compiledText = [compileNote(entity)];
  } else if (entity.type === 'collection') {
    compiledText = compileCollection({ collection: entity, getNodesByRefs, onMissingRef });
  }

  return {
    content: compiledText,
    hash: getSha1Hash(JSON.stringify(compiledText)),
  };
}

function formatData(entity: TEntity, depth: number): TFormatted {
  const {
    id,
    meta: { name, withTitle },
  } = entity;

  return {
    id,
    depth,
    withTitle,
    originalName: name,
    name: parser(name),
    text: '',
    originalContent: '',
  };
}

export function compileNote(note: TNote, depth = 0): TFormatted {
  return {
    ...formatData(note, depth),
    // text: parser(note.content),
    originalContent: note.content,
  };
}

interface ICollectionCompileParams {
  collection: TCollection;
  getNodesByRefs: TGetNodesByRefs;
  /**
   * Глубина самой коллекции. Детям уходит на единицу больше — но только если эта
   * коллекция действительно даёт заголовок; контейнер без withTitle уровень не занимает.
   * По умолчанию — корень.
   */
  depth?: number;
  /** См. TOnMissingRef: без него пропущенная ссылка неотличима от пустоты. */
  onMissingRef?: TOnMissingRef;
}

export function compileCollection({
  collection,
  getNodesByRefs,
  depth = 0,
  onMissingRef,
}: ICollectionCompileParams) {
  const compiled: TFormatted[] = [];

  if (collection.meta.withTitle) {
    const metaData = formatData(collection, depth);
    compiled.push(metaData);
  }

  // Уровень занимает только коллекция, которая реально даёт заголовок.
  // Организационный контейнер с выключенным withTitle пропускать уровень не должен:
  // иначе документ уходит с H1 сразу на H3, а Word строит по этому оглавление.
  const childDepth = collection.meta.withTitle ? depth + 1 : depth;

  const children = getNodesByRefs(collection.children);

  children.forEach((child, index) => {
    if (!child) {
      onMissingRef?.(collection.children[index]);

      return;
    }

    if (child.meta.isHidden) {
      return;
    }

    if (child.type === 'note') {
      const compiledNote = compileNote(child, childDepth);
      compiled.push(compiledNote);

      return;
    }

    if (child.type === 'collection') {
      const compiledChildren = compileCollection({
        collection: child,
        getNodesByRefs,
        depth: childDepth,
        onMissingRef,
      });
      compiled.push(...compiledChildren);
    }
  });

  return compiled;
}
